<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Pay Bill</span>
            </nav>
            <h1 class="page-title">Pay Your Bill</h1>
            <p class="page-subtitle">Look up and pay your county bills quickly and securely</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  </div>
                  <h3>Bill Payment</h3>
                </div>
                <p class="info-description">
                  Pay your county bills including permits, licenses, fees, and other charges. Enter your bill number to view details and make payment.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>M-Pesa Payment</strong>
                      <p>Pay instantly via mobile money</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Instant Confirmation</strong>
                      <p>Real-time payment verification</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                    </div>
                    <div>
                      <strong>Digital Receipt</strong>
                      <p>Print or download your receipt</p>
                    </div>
                  </div>
                </div>

                <div class="info-help">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    Where to Find Your Bill Number
                  </h4>
                  <ul>
                    <li>Check your bill notification SMS</li>
                    <li>Look at your physical bill document</li>
                    <li>Find it in your email notification</li>
                    <li>Contact county offices if needed</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Right Side - Form -->
            <div class="form-panel">
              <!-- Step 1: Enter Bill Number -->
              <div v-if="!billResult" class="form-card">
                <div class="form-card-header">
                  <div class="step-badge">
                    <span class="step-number">1</span>
                    <span class="step-text">Enter Bill Number</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <p class="form-intro">
                    Enter your bill number to retrieve your bill details and proceed with payment.
                  </p>

                  <div class="bill-lookup-form">
                    <!-- Bill Number Input -->
                    <div class="form-group">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                        </svg>
                        Bill Number
                      </label>
                      <div class="input-with-icon large-input">
                        <input
                            v-model="billNo"
                            type="text"
                            class="form-input"
                            placeholder="Enter your bill number"
                            @keyup.enter="getBill"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                        </span>
                      </div>
                      <span class="form-hint">Your bill number can be found on your bill document or SMS notification</span>
                    </div>

                    <!-- Bill Types Info -->
                    <div class="bill-types-info">
                      <h4>Accepted Bill Types</h4>
                      <div class="bill-types-grid">
                        <div class="bill-type-item">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="3" y1="9" x2="21" y2="9"></line>
                            <line x1="9" y1="21" x2="9" y2="9"></line>
                          </svg>
                          <span>Business Permits</span>
                        </div>
                        <div class="bill-type-item">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                          </svg>
                          <span>Land Rates</span>
                        </div>
                        <div class="bill-type-item">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="1" y="3" width="15" height="13" rx="2"></rect>
                            <circle cx="5.5" cy="18.5" r="2.5"></circle>
                            <circle cx="12.5" cy="18.5" r="2.5"></circle>
                          </svg>
                          <span>Parking Fees</span>
                        </div>
                        <div class="bill-type-item">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                          </svg>
                          <span>House Rent</span>
                        </div>
                        <div class="bill-type-item">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                          </svg>
                          <span>Construction</span>
                        </div>
                        <div class="bill-type-item">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="1" x2="12" y2="23"></line>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                          </svg>
                          <span>Other Fees</span>
                        </div>
                      </div>
                    </div>

                    <!-- Error Message -->
                    <div v-if="message" class="alert alert-error">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      <span>{{ message }}</span>
                      <button class="alert-close" @click="message = null">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>

                    <button
                        @click="getBill"
                        class="btn btn-primary btn-block btn-lg"
                        :disabled="!billNo || loading"
                    >
                      <div v-if="loading" class="spinner white"></div>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <span>{{ loading ? 'Looking up bill...' : 'Check Bill Details' }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Step 2: Bill Details & Payment -->
              <div v-if="billResult" class="form-card">
                <div class="form-card-header">
                  <button class="back-btn" @click="goBack">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                  </button>
                  <div class="step-badge">
                    <span class="step-number">2</span>
                    <span class="step-text">View Details & Pay</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <!-- Bill Header -->
                  <div class="bill-header">
                    <div class="bill-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                      </svg>
                    </div>
                    <div class="bill-info">
                      <span class="bill-number-label">Bill Number</span>
                      <h2 class="bill-number-display">{{ billDetails.billNo }}</h2>
                      <span class="bill-type-badge">{{ billDetails.incomeTypeDescription }}</span>
                    </div>
                  </div>

                  <!-- Bill Details -->
                  <div class="details-section">
                    <h3 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                      Bill Details
                    </h3>

                    <div class="details-grid">
                      <div class="detail-item">
                        <span class="detail-label">Description</span>
                        <span class="detail-value">{{ billDetails.incomeTypeDescription }}</span>
                      </div>

                      <div class="detail-item">
                        <span class="detail-label">Item</span>
                        <span class="detail-value">{{ billDetails.feeDescription || 'N/A' }}</span>
                      </div>

                      <div class="detail-item">
                        <span class="detail-label">Billed Amount</span>
                        <span class="detail-value">KES {{ formatNumber(billDetails.detailAmount) }}</span>
                      </div>

                      <div class="detail-item">
                        <span class="detail-label">Bill Date</span>
                        <span class="detail-value">{{ formatDate(billDetails.dateCreated) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Balance Card -->
                  <div class="balance-card" :class="{ 'has-balance': parseFloat(billDetails.billBalance) > 0 }">
                    <div class="balance-header">
                      <span class="balance-label">Current Balance</span>
                      <span class="balance-status" v-if="parseFloat(billDetails.billBalance) === 0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        Paid
                      </span>
                      <span class="balance-status pending" v-else>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        Pending
                      </span>
                    </div>
                    <div class="balance-amount">
                      <span class="currency">KES</span>
                      <span class="amount">{{ formatNumber(billDetails.billBalance) }}</span>
                    </div>
                  </div>

                  <!-- Bill Items (if multiple) -->
                  <div v-if="billInfo && billInfo.length > 1" class="bill-items-section">
                    <h3 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="8" y1="6" x2="21" y2="6"></line>
                        <line x1="8" y1="12" x2="21" y2="12"></line>
                        <line x1="8" y1="18" x2="21" y2="18"></line>
                        <line x1="3" y1="6" x2="3.01" y2="6"></line>
                        <line x1="3" y1="12" x2="3.01" y2="12"></line>
                        <line x1="3" y1="18" x2="3.01" y2="18"></line>
                      </svg>
                      Bill Items
                    </h3>
                    <div class="bill-items-list">
                      <div v-for="(item, index) in billInfo" :key="index" class="bill-item-row">
                        <span class="item-desc">{{ item.feeDescription || item.incomeTypeDescription }}</span>
                        <span class="item-amount">KES {{ formatNumber(item.billTotal || item.detailAmount) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Payment Form (only show if balance > 0) -->
                  <div v-if="parseFloat(billDetails.billBalance) > 0" class="payment-form">
                    <h3 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                      Make Payment
                    </h3>

                    <!-- Amount to Pay -->
                    <div class="form-group">
                      <label class="form-label">Amount to Pay</label>
                      <div class="input-with-currency">
                        <span class="currency-badge">KES</span>
                        <input
                            v-model="amountToPay"
                            type="number"
                            class="form-input with-currency"
                            placeholder="Enter amount"
                            :max="billDetails.billBalance"
                        />
                      </div>
                      <div class="quick-amount-btns">
                        <button
                            class="quick-btn"
                            @click="amountToPay = billDetails.billBalance"
                            :class="{ active: amountToPay == billDetails.billBalance }"
                        >
                          Full Balance
                        </button>
                        <button
                            v-if="parseFloat(billDetails.billBalance) >= 1000"
                            class="quick-btn"
                            @click="amountToPay = Math.floor(billDetails.billBalance / 2)"
                        >
                          Half (KES {{ formatNumber(Math.floor(billDetails.billBalance / 2)) }})
                        </button>
                      </div>
                    </div>

                    <!-- M-Pesa Phone Number -->
                    <div class="form-group">
                      <label class="form-label">M-Pesa Phone Number</label>
                      <div class="input-with-icon">
                        <input
                            v-model="phoneNumber"
                            type="tel"
                            class="form-input"
                            placeholder="e.g., 0712345678"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                            <line x1="12" y1="18" x2="12.01" y2="18"></line>
                          </svg>
                        </span>
                      </div>
                      <span class="form-hint">You'll receive an STK push to this number</span>
                    </div>

                    <button
                        @click="customerPayBillOnline"
                        class="btn btn-success btn-block btn-lg"
                        :disabled="!amountToPay || !phoneNumber || paymentLoading"
                    >
                      <div v-if="paymentLoading" class="spinner white"></div>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                      <span>{{ paymentLoading ? 'Processing...' : `Pay KES ${formatNumber(amountToPay || 0)}` }}</span>
                    </button>
                  </div>

                  <!-- Paid Status Message -->
                  <div v-else class="paid-status">
                    <div class="paid-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <h3>Bill Fully Paid</h3>
                    <p>This bill has been fully settled. No payment is required.</p>
                    <button @click="goBack" class="btn btn-secondary">
                      Look Up Another Bill
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Payment Modal -->
    <transition name="modal">
      <div v-if="payConfirmationPop" class="modal-overlay">
        <div class="modal-container payment-modal">
          <!-- Payment Header -->
          <div class="payment-modal-header" :class="paymentReceived ? 'success' : 'pending'">
            <div v-if="!paymentReceived" class="payment-loader">
              <div class="loader-ring"></div>
            </div>
            <div v-else class="payment-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <p class="payment-status-text">
              {{ paymentReceived ? 'Payment Successful!' : `Pending ${billDetails.incomeTypeDescription} Payment` }}
            </p>
            <h2 class="payment-amount">KES {{ formatNumber(amountToPay) }}</h2>
          </div>

          <!-- Payment Body -->
          <div class="payment-modal-body">
            <!-- Instructions (while waiting) -->
            <div v-if="!paymentReceived" class="payment-instructions">
              <h4>Payment Procedure</h4>
              <p class="instruction-subtitle">Follow the steps below to complete your payment</p>
              <ol>
                <li>
                  <span class="step-num">1</span>
                  Check for a <strong>payment pop up</strong> on your phone
                </li>
                <li>
                  <span class="step-num">2</span>
                  Enter your <strong>M-Pesa PIN</strong> and click OK
                </li>
                <li>
                  <span class="step-num">3</span>
                  Wait for <strong>M-Pesa confirmation SMS</strong>
                </li>
                <li>
                  <span class="step-num">4</span>
                  Wait up to <strong>45 seconds</strong> as we process your transaction
                </li>
              </ol>
              <div class="waiting-indicator">
                <div class="pulse-dot"></div>
                <span>Waiting for payment confirmation...</span>
              </div>
            </div>

            <!-- Success Content -->
            <div v-if="paymentReceived" class="payment-success">
              <h4>Thank You {{ transaction.names }}!</h4>
              <p class="success-message">Payment was successfully received.</p>

              <div class="success-details">
                <div class="success-row">
                  <span>Transaction Code</span>
                  <strong>{{ transaction.transaction_code }}</strong>
                </div>
                <div class="success-row">
                  <span>Bill Number</span>
                  <strong>{{ billDetails.billNo }}</strong>
                </div>
                <div class="success-row">
                  <span>Description</span>
                  <strong>{{ billDetails.incomeTypeDescription }}</strong>
                </div>
                <div class="success-row">
                  <span>Amount Paid</span>
                  <strong>KES {{ formatNumber(amountToPay) }}</strong>
                </div>
              </div>

              <div class="success-actions">
                <button @click="printReceipt" class="btn btn-primary">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                  Print Receipt
                </button>
                <button @click="closePaymentModal" class="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <FooterComponent />
  </div>
</template>

<script>
import { paysol, biller, execute, execute2, authUrl } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "PayBill",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      message: null,
      loading: false,
      billResult: false,
      payConfirmationPop: false,
      paymentLoading: false,
      paymentReceived: false,

      billNo: '',
      phoneNumber: null,
      amountToPay: null,

      county: {
        id: null,
        countyName: null
      },

      billDetails: {
        id: '',
        billNo: '',
        incomeTypeID: '',
        incomeTypeDescription: '',
        feeDescription: '',
        detailAmount: '',
        receiptAmount: '',
        billBalance: '',
        dateCreated: '',
        status: ''
      },

      billInfo: [],

      bank: {
        id: null,
        bankID: null,
        bank: null,
        payBill: null
      },

      transaction: {
        id: null,
        transaction_code: null,
        names: null,
        msisdn: null
      },

      receiptDetails: {
        receiptNo: null
      }
    };
  },

  mounted() {
    this.getCounty();
    // Check for bill/invoice number in URL query params
    // Works with hash routing: /#/pay-bill?billNo=XXX&phone=XXX
    // Supports: ?billNo=XXX, ?invoiceNo=XXX, ?bill=XXX, ?invoice=XXX, ?ref=XXX
    const query = this.$route.query;
    const billNumber = query.billNo ||
        query.invoiceNo ||
        query.bill ||
        query.invoice ||
        query.ref;

    // Also check for phone number parameter
    const phone = query.phone || query.phoneNumber || query.msisdn;
    if (phone) {
      this.phoneNumber = phone;
    }

    if (billNumber) {
      this.billNo = billNumber;
      this.getBill();
    }
  },

  methods: {
    formatNumber(num) {
      if (!num) return '0';
      return new Intl.NumberFormat('en-KE').format(num);
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-KE', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return dateStr;
      }
    },

    goBack() {
      this.billResult = false;
      this.message = null;
      this.billNo = '';
      this.amountToPay = null;
      this.phoneNumber = null;
    },

    closePaymentModal() {
      this.payConfirmationPop = false;
      this.paymentReceived = false;
      this.paymentLoading = false;
      // Refresh bill details
      this.getBill();
    },

    // API Methods
    getCounty() {
      const data = new FormData();
      data.append("function", "getCounty");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.county = res.data.data.county;
            }
          })
          .catch((e) => {
            console.error(e.message);
          });
    },

    getBill() {
      if (!this.billNo) {
        this.message = "Please enter a bill number";
        return;
      }

      this.message = null;
      this.loading = true;

      const data = new FormData();
      data.append("function", "getBill");
      data.append("billNo", this.billNo);

      execute(data, biller)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.billResult = true;
              this.billDetails = res.data.data.billDetails;
              this.billInfo = res.data.data.billInfo || [];
              this.amountToPay = this.billDetails.billBalance;
              this.bank = res.data.data.bank;
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message;
          });
    },

    customerPayBillOnline() {
      if (!this.phoneNumber) {
        this.message = "Please enter your M-Pesa phone number";
        return;
      }

      if (!this.amountToPay || this.amountToPay <= 0) {
        this.message = "Please enter a valid amount";
        return;
      }

      this.paymentLoading = true;
      this.payConfirmationPop = true;

      const data = new FormData();
      data.append("function", "customerPayBillOnline");
      data.append("payBillNumber", this.bank.payBill);
      data.append("amount", this.amountToPay);
      data.append("accountReference", this.billDetails.billNo);
      data.append("transactionDesc", this.billDetails.billNo);
      data.append("phoneNumber", this.phoneNumber);
      data.append("token", "im05WXYH2rwRruPjCICieOs8m4E8IoltnDEhyPUv6bnB9cU60gD48SnJPC6oh7EpsPaAUGC8wqIdtVVjGlWLxqFssshxMHxHjEQJ");

      execute2(data, paysol)
          .then((res) => {
            this.paymentLoading = false;
            if (res.data.success) {
              const intervalId = setInterval(() => {
                this.checkPayment()
                    .then((result) => {
                      if (result) {
                        clearInterval(intervalId);
                      }
                    })
                    .catch((error) => {
                      console.error(error);
                    });
              }, 2000);
            } else {
              this.payConfirmationPop = false;
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.paymentLoading = false;
            this.payConfirmationPop = false;
            this.message = e.message;
          });
    },

    checkPayment() {
      const data = new FormData();
      data.append("function", "checkPayment");
      data.append("accountRef", this.billDetails.billNo);

      return new Promise((resolve, reject) => {
        execute(data, biller)
            .then((res) => {
              if (res.data.success) {
                this.paymentLoading = false;
                this.transaction = res.data.data.transaction;
                this.receiptDetails = res.data.data.receipt.receiptDetails;
                this.paymentReceived = true;
                resolve(true);
              } else {
                resolve(false);
              }
            })
            .catch((error) => {
              reject(error);
            });
      });
    },

    printReceipt() {
      if (!this.receiptDetails.receiptNo) {
        alert("Receipt not available yet. Please try again.");
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: this.receiptDetails.receiptNo
      });
      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Content Section */
.content-section {
  padding: 48px 0 80px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 32px;
  align-items: start;
}

/* Info Panel */
.info-panel {
  position: sticky;
  top: 96px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
}

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.info-description {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
}

.info-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
}

.feature-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #ede9fe; color: #7c3aed; }

.feature-item strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 2px;
}

.feature-item p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.info-help {
  padding: 20px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
}

.info-help h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 12px;
}

.info-help ul {
  margin: 0;
  padding-left: 24px;
}

.info-help li {
  font-size: 13px;
  color: #3b82f6;
  margin-bottom: 6px;
}

/* Form Panel */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-card-header {
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.step-badge {
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-badge .step-number {
  width: 28px;
  height: 28px;
  background: #0f4c81;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
}

.step-badge .step-text {
  font-weight: 600;
  color: #1e293b;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.form-card-body {
  padding: 32px;
}

.form-intro {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Form Elements */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.input-with-icon {
  position: relative;
}

.input-with-icon.large-input .form-input {
  padding: 18px 16px;
  padding-right: 48px;
  font-size: 17px;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  padding-right: 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.input-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-hint {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
}

/* Input with Currency */
.input-with-currency {
  position: relative;
  display: flex;
}

.currency-badge {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 60px;
  background: #f1f5f9;
  border: 2px solid #e2e8f0;
  border-right: none;
  border-radius: 10px 0 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #64748b;
  font-size: 14px;
}

.form-input.with-currency {
  padding-left: 72px;
  border-radius: 10px;
}

/* Quick Amount Buttons */
.quick-amount-btns {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.quick-btn {
  flex: 1;
  padding: 10px 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-btn:hover {
  background: #eff6ff;
  border-color: #0f4c81;
  color: #0f4c81;
}

.quick-btn.active {
  background: #0f4c81;
  border-color: #0f4c81;
  color: white;
}

/* Bill Types Info */
.bill-types-info {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.bill-types-info h4 {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.bill-types-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.bill-type-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: white;
  border-radius: 8px;
  font-size: 13px;
  color: #64748b;
}

.bill-type-item svg {
  color: #94a3b8;
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: inherit;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block { width: 100%; }
.btn-lg { padding: 16px 32px; font-size: 16px; }

.btn-primary {
  background: #0f4c81;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0a3459;
}

.btn-success {
  background: #059669;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #047857;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.white {
  border-color: rgba(255,255,255,0.3);
  border-top-color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Bill Header */
.bill-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  margin-bottom: 24px;
}

.bill-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.bill-number-label {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.bill-number-display {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #1e293b;
  margin: 4px 0;
}

.bill-type-badge {
  font-size: 13px;
  color: #2563eb;
  font-weight: 600;
  background: rgba(37, 99, 235, 0.15);
  padding: 4px 10px;
  border-radius: 6px;
}

/* Details Section */
.details-section {
  margin-bottom: 24px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-label {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

/* Balance Card */
.balance-card {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 2px solid #6ee7b7;
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 24px;
}

.balance-card.has-balance {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-color: #fbbf24;
}

.balance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.balance-label {
  font-size: 14px;
  font-weight: 600;
  color: #047857;
}

.balance-card.has-balance .balance-label {
  color: #92400e;
}

.balance-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #059669;
}

.balance-status.pending {
  color: #d97706;
}

.balance-amount {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.balance-amount .currency {
  font-size: 18px;
  font-weight: 600;
  color: #047857;
}

.balance-card.has-balance .balance-amount .currency {
  color: #92400e;
}

.balance-amount .amount {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
  color: #065f46;
}

.balance-card.has-balance .balance-amount .amount {
  color: #78350f;
}

/* Bill Items Section */
.bill-items-section {
  margin-bottom: 24px;
}

.bill-items-list {
  background: #f8fafc;
  border-radius: 10px;
  overflow: hidden;
}

.bill-item-row {
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
}

.bill-item-row:last-child {
  border-bottom: none;
}

.item-desc {
  font-size: 14px;
  color: #475569;
}

.item-amount {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

/* Payment Form */
.payment-form {
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

/* Paid Status */
.paid-status {
  text-align: center;
  padding: 40px 20px;
}

.paid-icon {
  width: 80px;
  height: 80px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  margin: 0 auto 20px;
}

.paid-status h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #059669;
  margin-bottom: 8px;
}

.paid-status p {
  color: #64748b;
  margin-bottom: 24px;
}

/* Payment Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 440px;
  width: 100%;
  overflow: hidden;
}

.payment-modal-header {
  padding: 40px 32px;
  text-align: center;
  color: white;
}

.payment-modal-header.pending {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
}

.payment-modal-header.success {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.payment-loader {
  margin-bottom: 20px;
}

.loader-ring {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255,255,255,0.2);
  border-top-color: white;
  border-radius: 50%;
  margin: 0 auto;
  animation: spin 1s linear infinite;
}

.payment-success-icon {
  width: 80px;
  height: 80px;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.payment-status-text {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.payment-amount {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
}

.payment-modal-body {
  padding: 32px;
}

.payment-instructions h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.instruction-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 20px;
}

.payment-instructions ol {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
}

.payment-instructions li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #475569;
}

.payment-instructions li:last-child {
  border-bottom: none;
}

.step-num {
  width: 24px;
  height: 24px;
  background: #eff6ff;
  color: #2563eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.waiting-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  background: #fffbeb;
  border-radius: 10px;
  font-size: 14px;
  color: #92400e;
}

.pulse-dot {
  width: 10px;
  height: 10px;
  background: #f59e0b;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}

.payment-success h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.success-message {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 20px;
}

.success-details {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  text-align: left;
}

.success-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
}

.success-row:last-child {
  border-bottom: none;
}

.success-row span {
  color: #64748b;
}

.success-row strong {
  color: #1e293b;
}

.success-actions {
  display: flex;
  gap: 12px;
}

.success-actions .btn {
  flex: 1;
}

/* Transitions */
.modal-enter-active, .modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(20px);
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-panel {
    position: static;
  }
}

@media (max-width: 768px) {
  .form-card-body {
    padding: 24px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .bill-header {
    flex-direction: column;
    text-align: center;
  }

  .page-title {
    font-size: 26px;
  }

  .success-actions {
    flex-direction: column;
  }

  .bill-types-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .balance-amount .amount {
    font-size: 28px;
  }

  .quick-amount-btns {
    flex-direction: column;
  }
}
</style>